import type { SelectChangeEvent } from '@mui/material/Select';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { PaginatedVenuesResponse } from '@lov/shared';
import type { VenueResponse } from '../features/venues/hooks/useVenues';
import { validatedApi } from '../lib/api/validated-client';

export function VenueSwitcherDropdown() {
  const theme = useTheme();
  const { venueId: routeVenueId } = useParams<{ venueId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: venues } = useQuery<PaginatedVenuesResponse, Error, VenueResponse[]>({
    queryKey: ['venues', 'my'],
    queryFn: () => validatedApi.get('/venues/my'),
    select: (r) => r.data,
    staleTime: 5 * 60 * 1000,
  });

  const currentVenueId = routeVenueId ?? venues?.[0]?.id;

  // Single-venue: static label styled for dark sidebar
  if (!venues || venues.length <= 1) {
    const singleVenue = venues?.[0];
    if (!singleVenue) return null;
    return (
      <Box
        sx={{
          px: 2,
          py: 0.75,
          fontSize: '0.8125rem',
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 600,
          color: theme.palette.sidebar.textActive,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          letterSpacing: '-0.003em',
        }}
      >
        {singleVenue.name}
      </Box>
    );
  }

  const handleSwitch = (newVenueId: string) => {
    if (!routeVenueId) {
      navigate(`/vendor/${newVenueId}/dashboard`, { replace: true });
      return;
    }
    const newPath = location.pathname.includes(`/${routeVenueId}/`)
      ? location.pathname.replace(`/${routeVenueId}/`, `/${newVenueId}/`)
      : `/vendor/${newVenueId}/dashboard`;
    navigate(newPath, { replace: true });
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    handleSwitch(event.target.value);
  };

  return (
    <FormControl fullWidth size="small" sx={{ px: 1.5, py: 0.5 }}>
      <Select
        value={currentVenueId ?? ''}
        onChange={handleChange}
        displayEmpty
        inputProps={{ 'aria-label': 'Select venue' }}
        sx={{
          fontSize: '0.8125rem',
          fontFamily: '"Poppins", sans-serif',
          fontWeight: 600,
          color: theme.palette.sidebar.textActive,
          // Transparent bg, white border — fits dark sidebar
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.sidebar.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.3)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FF8800',
            borderWidth: '1.5px',
          },
          // Dropdown chevron icon
          '& .MuiSelect-icon': {
            color: theme.palette.sidebar.text,
          },
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.75,
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: theme.palette.sidebar.hover,
          },
          borderRadius: 2,
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: '#1e1640',
              border: `1px solid ${theme.palette.sidebar.border}`,
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              '& .MuiMenuItem-root': {
                fontFamily: '"Poppins", sans-serif',
                fontSize: '0.8125rem',
                color: theme.palette.sidebar.textActive,
                borderRadius: 1,
                mx: 0.5,
                '&:hover': { backgroundColor: theme.palette.sidebar.hover },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255,136,0,0.15)',
                  color: '#FF8800',
                  '&:hover': { backgroundColor: 'rgba(255,136,0,0.2)' },
                },
              },
            },
          },
        }}
        renderValue={(value) => {
          const venue = venues.find((v) => v.id === value);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {venue?.name || 'Select Venue'}
            </Box>
          );
        }}
      >
        {venues.map((venue) => (
          <MenuItem key={venue.id} value={venue.id}>
            <ListItemText
              primary={venue.name}
              primaryTypographyProps={{
                fontSize: '0.8125rem',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 500,
              }}
            />
            <Chip
              label={venue.venueType
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (c: string) => c.toUpperCase())}
              size="small"
              sx={{
                ml: 1,
                height: 20,
                fontSize: '0.625rem',
                fontWeight: 600,
                fontFamily: '"Poppins", sans-serif',
                backgroundColor: 'rgba(255,136,0,0.15)',
                color: '#FF8800',
                border: '1px solid rgba(255,136,0,0.3)',
              }}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
